import React from "react";
import { MapPin, Globe, MessageCircle, Bookmark, Loader2 } from "lucide-react";
import { getPlatformIcon, getPlatformStyle } from "../../../ui/SocialMediaIcons";


const ProfileBanner = ({
  exhibitor,
  showInquiries,
  onInquiryClick,
  appointmentDate,
  hasPromo,
  isFavorited,
  isFavLoading,
  onFavoriteToggle,
}) => {

  return (
    <div className="rounded-b-xl overflow-hidden border border-slate-100">
      <div className="relative h-[130px] sm:h-[180px] md:h-[258px]">
        {exhibitor.banner_url ? (
          <img
            src={exhibitor.banner_url}
            alt="banner"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-200 to-purple-300" />
        )}

        <div className="absolute -bottom-[50px] left-4 right-4 sm:left-6 sm:right-6 flex items-end justify-between sm:justify-start sm:gap-3 z-10 pointer-events-none">
          <div
            className="w-[100px] h-[100px] sm:w-[110px] sm:h-[110px]
          bg-white rounded-xl border border-slate-200
          flex items-center justify-center overflow-hidden h-full shadow-sm pointer-events-auto"
          >
            <img
              src={exhibitor.logo || "https://placehold.co/400x400?text=Logo"}
              alt="logo"
              className="w-full h-full object-contain"
            />
          </div>

          <button
            onClick={onFavoriteToggle}
            className={`w-9 h-9 mb-1 flex items-center justify-center rounded-lg border transition-all pointer-events-auto ${
              isFavorited
                ? "border-orange-200 bg-orange-50 text-orange-600 shadow-sm"
                : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700"
            }`}
            disabled={isFavLoading}
            title={isFavorited ? "Remove from My List" : "Add to My List"}
          >
            {isFavLoading ? (
              <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
            ) : (
              <Bookmark className={`w-4 h-4 ${isFavorited ? "fill-current" : ""}`} />
            )}
          </button>
        </div>
      </div>

      {/* Action Row: Social icons and other actions */}
      <div className="relative z-10 flex items-center justify-end gap-3 px-4 sm:px-6 pt-2 h-11">

        {/* Social icons — desktop only (hidden on mobile) */}
        {exhibitor.social_links?.length > 0 && (
          <div className="hidden sm:flex items-center gap-2">
            {exhibitor.social_links.map((link, i) => {
              const style = getPlatformStyle(link.platform);
              const formattedUrl = link.url?.startsWith("http")
                ? link.url
                : `https://${link.url}`;
              
              return (
                <a
                  key={i}
                  href={formattedUrl}
                  target="_blank"
                  rel="noreferrer"
                  title={link.platform}
                  style={{ backgroundColor: style.bg, color: style.color }}
                  className="w-8 h-8 rounded-full flex items-center justify-center
                  transition-opacity hover:opacity-80 shrink-0 cursor-pointer"
                >
                  {getPlatformIcon(link.platform)}
                </a>
              );
            })}
          </div>
        )}
      </div>

      <div className="px-4 sm:px-6 pt-10 pb-2">
        <h1 className="text-xl sm:text-2xl font-semibold text-slate-800 capitalize">
          {exhibitor.name}
        </h1>
        {exhibitor.tagline && (
          <p className="text-sm text-slate-500 mt-0.5">{exhibitor.tagline}</p>
        )}
        {exhibitor.company_description && (
          <p className="text-sm text-slate-500 mt-1 line-clamp-3">
            {exhibitor.company_description}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-slate-500">
          {exhibitor.booth_number && (
            <span className="flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" />
              Booth No:
              <strong className="text-slate-800 font-semibold">
                {exhibitor.booth_number}
              </strong>
            </span>
          )}
          {exhibitor.website && (
            <a
              href={
                exhibitor.website.startsWith("http")
                  ? exhibitor.website
                  : `https://${exhibitor.website}`
              }
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-blue-600 hover:underline"
            >
              <Globe className="w-3.5 h-3.5" />
              View Website
            </a>
          )}
        </div>

        {/* Social icons — mobile only (hidden on desktop) */}
        {exhibitor.social_links?.length > 0 && (
          <div className="flex sm:hidden items-center gap-2 mt-3 flex-wrap">
            {exhibitor.social_links.map((link, i) => {
              const style = getPlatformStyle(link.platform);
              const formattedUrl = link.url?.startsWith("http")
                ? link.url
                : `https://${link.url}`;
              return (
                <a
                  key={i}
                  href={formattedUrl}
                  target="_blank"
                  rel="noreferrer"
                  title={link.platform}
                  style={{ backgroundColor: style.bg, color: style.color }}
                  className="w-8 h-8 rounded-full flex items-center justify-center
                  transition-opacity hover:opacity-80 shrink-0"
                >
                  {getPlatformIcon(link.platform)}
                </a>
              );
            })}
          </div>
        )}
      </div>

      {showInquiries && (
        <div className="px-4 sm:px-6 mt-4 mb-2">
          <button onClick={onInquiryClick} className="site-color-btn">
            <MessageCircle className="w-4 h-4" />
            Submit Inquiry
          </button>
        </div>
      )}

      {appointmentDate && (
        <div
          className="mx-4 sm:mx-6 mb-4 mt-3 flex items-center gap-2.5
        px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800"
        >
          <span
            className="w-4 h-4 shrink-0 rounded-full bg-blue-500 flex items-center
          justify-center text-white text-[10px] font-bold"
          >
            i
          </span>
          You can book appointment with exhibitors from {appointmentDate}
        </div>
      )}
    </div>
  );
};

export default ProfileBanner;